import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import { SessionStatus, sessionAcknowledgePayment } from '@/store/slices/session';
import { useBeginCheckout, usePurchaseConversion } from '@/utils/analytics/useCheckoutConversions';
import { GATE2WAY_PROVIDER, STRIPE_PROVIDER, type CheckoutProvider } from '@/utils/session-invoice';
import { readStartMarketHandoff } from '@/utils/start-properties-market';
import type { PaymentAcknowledgement } from '@/utils/session-payment';
import { useResumePaidSession } from '@/nova/hooks/useResumePaidSession';
import {
  CheckoutBody,
  useCheckout,
} from '@/nova/pages/configurator-v0-mx/steps/finish/components/payment';
import { resolvePaymentSurface } from '@/nova/pages/configurator-v0-mx/steps/finish/components/payment/payment-methods';
import type { CheckoutStoreApi } from '@/nova/pages/configurator-v0-mx/steps/finish/components/payment/store-api';
import { useConfigurator, WIZARD_STEPS } from '../../../store/configurator';
import { isOneTimeBilling } from '../../../data/billing';
import { PaymentCheckout, PaymentSuccess, CheckoutError } from '../../../steps/finish/components/payment';
import { useCheckoutEmail } from '../../../steps/finish/components/payment/useCheckoutEmail';
import { StatusBox, StatusText } from '../../../steps/finish/components/payment/PaymentCheckout.styles';
import {
  Screen,
  PaymentScrim,
  PaymentSheet,
  PaymentHeader,
  PaymentTitle,
  PaymentClose,
  PaymentBody,
} from '../MobileFinishFlow/MobileFinishFlow.styles';

const CloseGlyph = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

/**
 * v0 mobile step 4, Payment, on its own screen (a full-height sheet). Resolves the payment surface
 * once (`resolvePaymentSurface`) and forks into one of three self-contained sheets, each a distinct
 * component so the Stripe-only path never spins up the shared `useCheckout` (which would create a
 * SECOND invoice alongside `PaymentCheckout`'s own):
 *
 *  - `fallback: 'demo'` (a card region with no live Stripe key) renders the Stripe-only sheet, whose
 *    `PaymentCheckout` falls back to the demo card checkout. `fallback: 'error'` (genuinely no rail)
 *    renders the local `CheckoutError`; it is defensive, as region rails are never empty today.
 *  - card only renders the unchanged Stripe-only `PaymentCheckout` sheet.
 *  - otherwise (SPEI-only or a real choice) renders the shared cross-flow `CheckoutBody`, driven by
 *    one `useCheckout` instance built from this flow's own store callbacks.
 */
export const MobilePaymentFlow = (): React.ReactElement => {
  const { currency, country, countryCode, billing, recordCheckout, recordCardCheckout, setPaymentMethod, flushCheckout } =
    useConfigurator();
  const handoff = React.useMemo(() => readStartMarketHandoff(), []);
  const railArgs = React.useMemo(
    () => ({ currency, or: countryCode ?? handoff.or, region: country }),
    [currency, countryCode, handoff.or, country],
  );
  const surface = React.useMemo(() => resolvePaymentSurface(railArgs), [railArgs]);

  // Conversion: InitiateCheckout / begin_checkout. This screen IS the checkout in v0 (same gate as
  // the desktop payment step): fire once billing is known and the entry probe has answered, unless
  // the session is already past checkout (paid invoice, or a post-payment status) and the screen
  // opens on the confirmation. Held HERE, above the fork below, so a surface that resolves late
  // cannot mount a second sheet and count a second time.
  const sessionId = useAppSelector((s) => s.session.sessionId);
  const sessionStatus = useAppSelector((s) => s.session.status);
  const resume = useResumePaidSession();
  const pastCheckout = resume.checkedOut || sessionStatus === SessionStatus.customization;
  const beginCheckout = useBeginCheckout(billing, sessionId);
  useEffect(() => {
    if (billing && !resume.resolving && !pastCheckout) beginCheckout();
  }, [billing, resume.resolving, pastCheckout, beginCheckout]);

  // 'demo' = a card region with no live Stripe key: fall back to the demo card checkout (the
  // Stripe-only sheet, whose `PaymentCheckout` renders `DemoCheckout`). 'error' is defensive:
  // region rails are never empty today, so a rail-less surface always resolves to 'demo'.
  if (surface.fallback === 'demo') return <StripeSheet />;
  if (surface.fallback === 'error') return <ErrorSheet />;

  const { methods } = surface;
  if (methods.length === 1 && methods[0] === STRIPE_PROVIDER) return <StripeSheet />;

  const store: CheckoutStoreApi = { recordCheckout, recordCardCheckout, setPaymentMethod, flushCheckout };
  // Keyed by currency: the offered rails depend on it, so useCheckout's state must not outlive it.
  return <SharedSheet key={currency} methods={methods} store={store} currency={currency} />;
};

/** Shared sheet chrome so every branch renders the same full-height sheet. */
function Sheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <Screen>
      <PaymentScrim $open>
        <PaymentSheet $open>
          <PaymentHeader>
            <PaymentTitle text={title} tag="paragraph" />
            <PaymentClose
              type="button"
              onClick={onClose}
              data-ga-id="cfg-mobile-payment-back"
              aria-label={t('Back')}
            >
              {CloseGlyph}
            </PaymentClose>
          </PaymentHeader>
          <PaymentBody>{children}</PaymentBody>
        </PaymentSheet>
      </PaymentScrim>
    </Screen>
  );
}

/** No usable rail (a card region with no live Stripe key): the sheet, a friendly error. */
function ErrorSheet(): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Sheet title={t('Payment')} onClose={() => navigate(WIZARD_STEPS[2].path)}>
      <CheckoutError />
    </Sheet>
  );
}

/**
 * The Stripe-only sheet: `PaymentCheckout` (the current live Stripe Elements look). Unchanged from
 * before the shared-checkout wiring; it owns its own email + invoice and never touches
 * `useCheckout`. Success is confirmed server-side (the session reaches a post-payment status).
 */
function StripeSheet(): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { emailAddress, billing } = useConfigurator();
  const [email, setEmail] = useCheckoutEmail(emailAddress);
  const [view, setView] = useState<'payment' | 'success'>('payment');

  const dispatch = useAppDispatch();
  const sessionStatus = useAppSelector((s) => s.session.status);
  const sessionId = useAppSelector((s) => s.session.sessionId);
  const invoiceStatus = useAppSelector((s) => s.session.invoiceStatus);
  useEffect(() => {
    if (sessionStatus === SessionStatus.payment_successful || sessionStatus === SessionStatus.customization) {
      setView('success');
    }
  }, [sessionStatus]);

  // A session can arrive here with its invoice ALREADY PAID — a card charge whose webhook landed
  // after the buyer closed the tab, or a 3DS return. The backend leaves such a session on
  // `configuration` until it is told, so this acknowledges it (once) and hands back the verdict.
  const resume = useResumePaidSession();
  useEffect(() => {
    if (resume.checkedOut) setView('success');
  }, [resume.checkedOut]);

  // Latched the moment Stripe accepts, so the checkout stays mounted through the acknowledge:
  // `resolving` goes true again while that call is in flight, and without this the sheet would
  // unmount mid-payment and blank the "Processing…" state.
  const [paying, setPaying] = React.useState(false);
  const onPaid = React.useCallback(async (): Promise<void> => {
    setPaying(true);
    if (sessionId) await dispatch(sessionAcknowledgePayment({ sessionId }));
    setView('success');
  }, [dispatch, sessionId]);

  // Conversion: Purchase (+ Subscribe), once per session. `paying` latches the moment Stripe
  // accepts the charge (or nothing was owed), the same instant desktop counts it; the status and
  // the invoice cover a session that arrives already paid. `customization` alone is NOT evidence.
  usePurchaseConversion(
    paying || sessionStatus === SessionStatus.payment_successful || invoiceStatus === 'paid',
    billing,
    sessionId,
  );

  const symbol = billing?.symbol ?? '$';
  const oneTime = isOneTimeBilling(billing);

  if (view === 'success') {
    // Card-only region: this sheet only ever confirms on payment evidence (a cleared Stripe charge
    // or a post-payment session status), so the transfer is always settled here.
    return <PaymentSuccess email={email} settled acknowledgement={resume.acknowledgement} />;
  }

  return (
    <Sheet title={t('Payment')} onClose={() => navigate(WIZARD_STEPS[2].path)}>
      {/* The checkout creates an invoice and loads Stripe the moment it mounts, so it must not
          start against a session whose invoice may already be paid. `resolving` always settles
          (settled flag + request timeout), and `paying` keeps it mounted through the acknowledge. */}
      {resume.resolving && !paying ? (
        <StatusBox>
          <StatusText text="Starting secure checkout…" tag="paragraph" />
        </StatusBox>
      ) : (
        <PaymentCheckout
          email={email}
          onEmail={setEmail}
          amount={billing?.dueToday ?? 0}
          grossAmount={billing?.total ?? 0}
          monthlyAmount={billing?.productFeatures ?? 0}
          currency={billing?.currency ?? 'USD'}
          currencySymbol={symbol}
          totalLabel={oneTime ? t('One time') : t('Total due today')}
          totalSub={oneTime ? undefined : t('(setup + first month)')}
          payLabel={t('Pay')}
          onSuccess={() => void onPaid()}
        />
      )}
    </Sheet>
  );
}

/**
 * The shared sheet (SPEI-only or a card/SPEI choice). Hosts the single `useCheckout` instance —
 * the `CheckoutBody` below it is presentational — mirroring the v0-mx mobile flow. A card charge is
 * settled on the spot; a SPEI transfer's confirmation is reached on the buyer's word (it settles
 * asynchronously).
 */
function SharedSheet({
  methods,
  store,
  currency,
}: {
  methods: CheckoutProvider[];
  store: CheckoutStoreApi;
  currency: string;
}): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { emailAddress, billing } = useConfigurator();
  const [done, setDone] = useState(false);

  const symbol = billing?.symbol ?? '$';
  const dueToday = billing?.dueToday ?? 0;
  const checkout = useCheckout({
    initialEmail: emailAddress,
    grossAmount: billing?.total ?? 0,
    currency,
    methods,
    store,
  });

  const sessionStatus = useAppSelector((s) => s.session.status);
  const invoiceStatus = useAppSelector((s) => s.session.invoiceStatus);
  // Settled = the money actually ARRIVED, asserted only from payment evidence — including, on the
  // CARD rail, Stripe having accepted the charge in this browser. `customization` is deliberately
  // excluded: on the SPEI rail the acknowledge advances the session to it on the buyer's WORD.
  // `nothingToPay` (a voucher covered it all, or the money was already in) counts as settled: there
  // is no pending transfer, so the confirmation must show the success copy, not the SPEI "we'll
  // confirm your transfer" pending copy. Matches the other three fork sites, which force settled here.
  const settled =
    checkout.cardSettled ||
    checkout.nothingToPay ||
    sessionStatus === SessionStatus.payment_successful ||
    invoiceStatus === 'paid';
  const checkedOut = settled || sessionStatus === SessionStatus.customization;

  // Conversion: Purchase (+ Subscribe), once per session, only on the payment evidence above.
  const sessionId = useAppSelector((s) => s.session.sessionId);
  usePurchaseConversion(settled, billing, sessionId);

  // Shared entry recovery: seeds the invoice status and acknowledges a session that arrives ALREADY
  // PAID (the backend waits to be told before advancing one).
  const resume = useResumePaidSession();

  useEffect(() => {
    if (checkedOut || resume.checkedOut) setDone(true);
  }, [checkedOut, resume.checkedOut]);

  // Nothing left to collect (voucher covered it all, or the money was already in): jump to the
  // confirmation rather than asking for a second payment.
  useEffect(() => {
    if (checkout.nothingToPay) setDone(true);
  }, [checkout.nothingToPay]);

  // Register the payment ("I've made the transfer" / a cleared charge), let the backend decide the
  // confirmation variant, and advance regardless so a failed call can't strand a paid buyer.
  const [acknowledgement, setAcknowledgement] = useState<PaymentAcknowledgement | null>(null);
  const onPaid = useCallback(async (): Promise<void> => {
    const result = await checkout.acknowledge();
    setAcknowledgement(result);
    setDone(true);
  }, [checkout]);

  const oneTime = isOneTimeBilling(billing);
  const onInstructions = checkout.view === 'instructions' && checkout.details;

  if (done) {
    return (
      <PaymentSuccess
        email={checkout.email || emailAddress}
        settled={settled}
        acknowledgement={acknowledgement ?? resume.acknowledgement}
      />
    );
  }

  return (
    <Sheet
      title={onInstructions ? (checkout.method === GATE2WAY_PROVIDER ? t('Pix') : t('Bank transfer')) : t('Payment')}
      onClose={onInstructions ? checkout.backToForm : () => navigate(WIZARD_STEPS[2].path)}
    >
      <CheckoutBody
        checkout={checkout}
        amount={dueToday}
        currency={currency}
        currencySymbol={symbol}
        totalLabel={oneTime ? t('One time') : t('Total due today')}
        totalSub={oneTime ? undefined : t('(setup + first month)')}
        onPaid={() => void onPaid()}
        compact
      />
    </Sheet>
  );
}

export default MobilePaymentFlow;
